<template>
  <main>
    <vf-header-provider v-if="useCustomHeader" :color-mode="headerStyle" :transparent-mode="overlayHeader" />

    <define-breadcrumbs>
      <vf-breadcrumbs
        v-if="breadcrumbs.length"
        :class="[brandClasses.breadcrumbs, { 'children:!c-white children:hover:!c-white': titleStyle === 'dark' }]"
        :items="breadcrumbs.slice(0, -1)"
        :last-step="showBreadcrumbsLastStep ? breadcrumbs[breadcrumbs.length - 1] : undefined"
        :max-items="breadcrumbsMaxItems"
      />
      <vf-breadcrumbs-skeleton v-else-if="pending" :class="brandClasses.breadcrumbs" />
    </define-breadcrumbs>

    <div :class="brandClasses.container">
      <vf-notification v-if="error" class="<lg:mb-4 lg:mt-4" :dismissible="false" type="error">
        {{ error.message }}
      </vf-notification>
      <breadcrumbs-component v-if="!showBreadcrumbsAfterEspot" />
    </div>

    <div v-if="$feature.enablePLPESpots" class="relative">
      <cms-section
        v-if="eSpots?.['above-grid']"
        :class="brandClasses.eSpotAboveGrid"
        page-context
        :section="eSpots?.['above-grid']"
      />
      <div :class="overlayTitle ? 'w-full bottom-0 absolute' : brandClasses.container">
        <breadcrumbs-component v-if="showBreadcrumbsAfterEspot" />
        <div v-if="!hideTitle" ref="categoryHeadingRef" class="mb-3 flex items-end between lg:mb-2">
          <div
            v-if="pending && !data"
            v-style="{ w: { sm: '8.75rem', md: '11.25rem' }, h: { sm: '1.25rem', md: '2rem' } }"
            class="skeleton"
            :class="brandClasses.categoryTitle"
          />
          <h1
            v-else-if="categoryTitle"
            :class="[brandClasses.categoryTitle, { 'c-white': titleStyle === 'dark' }]"
          >
            {{ categoryTitle }}
          </h1>
          <p aria-live="polite" :class="[brandClasses.productCount, { 'c-white': titleStyle === 'dark' }]">
            {{ data?.total || 0 }} {{ $pluralize($t.products, data?.total || 0) }}
          </p>
        </div>
      </div>
    </div>
    <product-finder-summary
      v-if="productFinderState && productFinderContent"
      :active-toggles="productFinderState.activeToggles"
      :content="productFinderContent"
      :content-checksum="productFinderState.contentChecksum"
      :finder-url="productFinderState.finderUrl"
    />
    <nav v-if="indexableFacets.length" aria-hidden="true" class="sr-only" data-test-id="plp-seo-links">
      <div
        v-for="{ facetLabel, href, text, value } in indexableFacets"
        :key="`${facetLabel}-${value}`"
        itemscope
        itemtype="https://schema.org/WebPage"
      >
        <a :href itemprop="url" tabindex="-1">
          <span itemprop="name">{{ text }}</span>
        </a>
      </div>
    </nav>
    <div :class="brandClasses.container">
      <div v-if="$feature.enablePLPESpots && showAboveGridBelowHeadingEspot">
        <cms-section
          v-if="eSpots?.['above-grid-below-heading']"
          :class="brandClasses.eSpotAboveGridBelowHeading"
          page-context
          :section="eSpots?.['above-grid-below-heading']"
        />
      </div>
      <div v-if="$feature.showFitFinderOnPLP && !fitFinderInFilters" id="fit-finder-plp" />
    </div>
    <product-list
      :key="productListKey"
      v-slot="{ cols }"
      :current-page="showPaginationLinks ? currentPage : undefined"
      :focused-products
      :grid-size-selector="$feature.showGridSizeSelectorOnPLP"
      :header-title="categoryTitle"
      :hide-rating="!$feature.showRatingOnPLP"
      :list="data"
      :next-page="showPaginationLinks ? nextPage : undefined"
      :next-page-loading="pending && infinityLoad"
      :overlay-title
      :pickup-filters="$feature.showPickupFiltersOnPLP"
      :previous-page="showPaginationLinks ? previousPage : undefined"
      :products-loading="pending && !infinityLoad"
      :side-filters="$feature.showSideFiltersOnPLP"
      :total-pages="showPaginationLinks ? totalPages : undefined"
      :tracking-category="productFinderExperienceName || enCategoryNameWithId"
      :tracking-list="productFinderExperienceName"
      @load-more="loadMore"
      @next="loadPage"
      @previous="loadPage"
    >
      <div
        v-for="section in inlineSectionMap(cols)"
        :key="section.name"
        v-style="{
          'grid-col': section.column,
          'grid-row': section.row,
          'display': section.display,
        }"
        class="relative"
        :class="[brandClasses.eSpotInlineGridWrapper]"
      >
        <lazy-cms-section
          :class="[getInGridEspotStyle(cols, section.width), { invisible: showEspotSkeleton }]"
          :full-height="!section.naturalHeight"
          :section
        />
        <div v-if="showEspotSkeleton" class="absolute-0 z-overlay skeleton" />
      </div>
    </product-list>

    <base-lazy-hydrate v-if="$feature.showBloomreachWidget" when="visible">
      <vf-recommendations-widget class="container" type="category" />
    </base-lazy-hydrate>

    <div :class="brandClasses.eSpotLazyAboveFooter">
      <cms-section
        v-if="!eSpotsPending && eSpots?.['below-grid']"
        lazy-media
        page-context
        :section="eSpots?.['below-grid']"
      />

      <cms-section
        v-if="!eSpotsPending && eSpots?.['lazy-above-footer']"
        lazy-media
        page-context
        :section="eSpots?.['lazy-above-footer']"
      />
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { ContentCategoryEspots } from '#root/api/clients/content/data-contracts'
import type { ProductCardExtended } from '#root/api/clients/product/data-contracts'
import type { Responsive } from '#types/common'
import type { FilterByContent } from '#types/components/cms/filter-by'
import type { FocusedProductContent } from '#types/components/cms/focused-product'
import type { ProductFinderContent } from '#types/components/cms/product-finder'
import type { ProductFinderState } from '#types/components/product/finder'
import type { InlineGridItem } from '#types/components/product/list'
import type { CacheHeaders } from '#types/plugin/cache'
import type { ProductCatalogQuery } from '#types/product'
import { GalleryContextKey } from '#commerce/contexts/gallery'
import { PlpAboveGridBelowHeadingEspotContextKey } from '#content/contexts/plp'

defineOptions({ name: 'ProductCategoryPage' })
definePageMeta({
  validate: async (route) => CATEGORY_URL_REGEX.test(route.path),
  middleware: 'plp-store-filter',
  keepalive: true,
  key: ({ path, query }) => `${path}-${query.page}`
})

const { getUserEventData } = useAnalytics()
const { grid, productsPerPage } = useAppConfig().components.product.list
const {
  brandClasses,
  breadcrumbsMaxItems,
  defaultSort,
  hideTitle,
  showAboveGridBelowHeadingEspot,
  showBreadcrumbsLastStep,
  showBreadcrumbsAfterEspot,
  useCustomHeader,
  fitFinderInFilters
} = useAppConfig().pages.plp
const cart = useCartStore()
const { getModal, getSections } = useCms()
const { injectFitFinder } = useFitFinder()
const { $feature, $gtm, $optimizely, $t, $viewport } = useNuxtApp()
const { trackEvent } = useOptimizely()
const route = useRoute()
const slug = useRouteParams('slug')
const filters = useRouteQuery('filters', '')
const page = useRouteQuery('page', '1')
const sort = useRouteQuery('sort', defaultSort)
const header = useHeaderStore()
const host = useHost()
const locale = useLocale()

onBeforeRouteUpdate((to, from) => {
  if (to.path !== from.path) header.resetScrollDirection()
})

/**
 * Validates and sanitizes page number from route query
 * Protects against:
 * - SSR timing issues (undefined)
 * - Malicious/invalid input (NaN, negative, non-integer)
 * @param value - Raw page value from route query
 * @returns Valid page number (minimum 1, integer)
 */
const getValidPageNumber = (value: string | undefined | null): number => {
  const num = Number(value)
  if (Number.isNaN(num) || num < 1) return 1
  return Math.floor(num) // Ensures integer
}

const storeFilter = $feature.showPickupFiltersOnPLP ? useRouteQuery<string | undefined>('storeFilter') : undefined
const pageTitle = isServer() ? '' : document.title

const [DefineBreadcrumbs, BreadcrumbsComponent] = createReusableTemplate()

const categoryId = getUrlId(route.path, 'Category')
const productFinderStorageKey = `productFinder_${locale}_${categoryId}`
const productFinderContent = ref<ProductFinderContent | null>(null)
const productFinderState = ref<ProductFinderState | null>(null)

const isProductFinderContent = (value: unknown): value is ProductFinderContent =>
  isObject(value)
  && value.type === 'VfCanvasProductFinder'
  && value.categoryId === categoryId
  && isArray(value.steps)

const isProductFinderState = (value: unknown): value is ProductFinderState =>
  isObject(value)
  && isString(value.finderId)
  && isString(value.finderUrl)
  && isString(value.activeStepId)
  && isArray(value.activeToggles)
  && value.activeToggles.every(isString)
  && (value.contentChecksum === undefined || isString(value.contentChecksum))

const parseProductFinderState = (value: string | null): ProductFinderState | null => {
  if (!value) return null

  try {
    const data = JSON.parse(value)
    return isProductFinderState(data) ? data : null
  }
  catch {
    return null
  }
}

const loadProductFinderSummary = async () => {
  const storedData = localStorage.getItem(productFinderStorageKey)

  productFinderContent.value = null
  productFinderState.value = null

  if (!storedData) return

  const state = parseProductFinderState(storedData)

  if (!state) return

  const content = await getModal<ProductFinderContent>(state.finderId)
  if (!isProductFinderContent(content)) return

  productFinderContent.value = content
  productFinderState.value = state
}

const eSpotsRequest = $feature.enablePLPESpots
  ? getSections.category(categoryId)
  : undefined
const eSpots = computed<ContentCategoryEspots['sectionsMap'] | undefined>(
  () => eSpotsRequest?.data.value?.content.sectionsMap
)
const eSpotsPending = eSpotsRequest?.pending ?? ref(false)
const headerStyle = computed(() => eSpotsRequest?.data.value?.content?.headerStyle || 'light')
const infinityLoad = ref(false)
const inlineEspots = computed<{ items?: InlineGridItem[] } | undefined>(
  () => eSpots.value?.['inline-grid'] as { items?: InlineGridItem[] } | undefined
)
const focusedProducts = computed<FocusedProductContent[]>(
  () => (eSpots.value?.['focused-product-espot']?.items || []) as FocusedProductContent[]
)
const isDeactivated = ref(false)
const overlayHeader = computed(() =>
  !!eSpotsRequest?.data.value?.content.overlayHeader
  && !!eSpots.value?.['above-grid']?.items?.length
)
const overlayTitle = computed(() => !!eSpotsRequest?.data.value?.content.overlayTitle)
const productListKey = ref(0)
const titleStyle = computed(() => eSpotsRequest?.data.value?.content?.titleStyle || 'light')
const categoryHeadingRef = ref<HTMLElement | null>(null)

const filtersQuery = computed(() => getFiltersQueryString(filters.value))
const trackingParams = computed(() => getTrackingQueryParams(route.query))
const customTrigger = computed(() => route.query.customTrigger as string)

const stickyHeaderHeight = computed(() => (
  header.height.navigation || header.height.header || 0
))

const query = reactive<ProductCatalogQuery>({
  cgid: categoryId,
  sort: sort.value,
  start: Math.max((getValidPageNumber(page.value) - 1) * productsPerPage, 0),
  count: productsPerPage,
  filters: filtersQuery.value,
  storeFilter: toValue(storeFilter),
  ...(customTrigger.value ? { customTrigger: customTrigger.value } : {}),
  // NEW: Dynamically spread ALL URL query parameters into the API payload
  ...trackingParams.value
})

const hasFilters = computed(() => !!query.filters || !!query.storeFilter || (query.sort && query.sort !== defaultSort))

// Get Referer for Bloomreach merchandising (SSR or CSR)
const reqHeaders = useRequestHeaders()
const referer = isServer()
  ? reqHeaders?.referer || (useRequestURL()?.href)
  : typeof window !== 'undefined' ? window.location.href : undefined

const { pending, data, error, refresh } = useApi().products.catalog(query, {
  lazy: true,
  key: `${categoryId}-${page.value}`,
  transform: (input) => ({
    ...input,
    filters: getUrlSafeFilters(input.filters),
    products: infinityLoad.value
      ? [
          ...(data.value?.products || []),
          ...input.products
        ]
      : input.products
  }),
  // Add Referer header for Bloomreach merchandising
  ...(referer && {
    headers: {
      Referer: referer
    }
  })
})

const plpSeoMeta = usePlpSeoMeta({ data, hasFilters, page: getValidPageNumber(page.value), filters })
const indexableFacets = computed(
  () => getIndexableFacetLinks(data.value?.title, route.path, data.value?.seo?.indexableFacets)
)

// Add product cache tags during SSR for the first page
if (isServer() && $feature.configCacheTagControlHeaders['Netlify-Cache-Tag'].enabled && data.value?.products?.length) {
  const cacheHeaders = useState<CacheHeaders>('cacheHeaders')

  if (cacheHeaders.value) {
    const productTags = data.value.products.map(({ id }) => `pdp_${id}`)
    cacheHeaders.value['Netlify-Cache-Tag']?.push(...productTags)
  }
}

if (error.value)
  throw createError({ statusCode: error.value.statusCode! >= 500 ? 500 : 404 })

const totalPages = computed(() => data.value ? Math.ceil(data.value.total / productsPerPage) : 0)
const currentPage = computed(() => Math.min(+page.value, totalPages.value))
const showPaginationLinks = computed(() => currentPage.value > 1)
const previousPage = computed(() => currentPage.value > 1 ? currentPage.value - 1 : undefined)
const nextPage = computed(() => currentPage.value < totalPages.value ? currentPage.value + 1 : undefined)

watchEffect(() => {
  if (error.value) showError(error.value)
})

useHead(() => ({
  titleTemplate: plpSeoMeta.seoTitle.value || pageTitle,
  link: plpSeoMeta.link.value,
  meta: plpSeoMeta.meta.value.map((meta) =>
    meta.name === 'robots' ? { ...meta, tagPriority: 'high' } : meta
  )
}))

const categoryTitle = computed(() => plpSeoMeta.displayCategoryTitle.value)
const breadcrumbs = computed(() => data.value?.breadcrumbs || [])
const enCategoryNameWithId = computed(() => formatTrackingCategory(
  data.value?.pageMetaData?.defaultPageName?.content,
  data.value?.url
))
const productFinderExperienceName = computed(() => {
  if (!import.meta.client || !route.fullPath) return undefined

  const experienceName = history.state?.productFinderExperienceName
  return typeof experienceName === 'string' ? experienceName : undefined
})
const productFinderTrackingContext = computed(() => (
  productFinderExperienceName.value
    ? {
        category: productFinderExperienceName.value,
        list: productFinderExperienceName.value,
        preserveListAndCategory: true
      }
    : {}
))

useSchemaOrg(() => [
  data.value?.pageJsonLd,
  data.value?.breadCrumbsJsonLd,
  data.value?.socialMediaJsonLd,
  data.value?.products
    ? getJsonLdForProductList(
        data.value.products,
        data.value?.url,
        host,
        { brand: $t.brandName, productList: $t.productList }
      )
    : undefined
])

const hasCartFilterEspot = (inlineEspots.value?.items ?? []).some(
  (item) => item.type === 'VfCanvasFilterBy' && hasByCartFilter((item as unknown as FilterByContent).filters)
)
const { cartItems: resolvedCartItems, showSkeleton: showEspotSkeleton } = useSsrSafeFilterState(hasCartFilterEspot)

const inlineSectionMap = (cols: Responsive<number>) => {
  const items = inlineEspots.value?.items
  if (!$feature.enableInlineGridEspots || !items) return []

  return getFilteredEspots(
    items,
    data.value?.products.length,
    cols,
    { route, cartItems: resolvedCartItems.value },
    (section) => !(section.hideOnSortOrFilter && hasFilters.value)
  )
}

const loadPage = (page: number) => navigateTo({
  ...route,
  query: {
    ...route.query,
    page
  }
})

const pushImpressions = (
  { allProductImpressions = false, immediate = false }:
  { allProductImpressions?: boolean, immediate?: boolean } = {}) => {
  if (!data.value?.products?.length) return

  const cols = { ...grid.cols, lg: $feature.showSideFiltersOnPLP ? grid.cols.lg - 1 : grid.cols.lg }
  type ImpressedProduct = ProductCardExtended & { index: number }

  let impressedProducts: ImpressedProduct[] = (data.value.products || []).map((product, index) => ({
    ...product,
    index
  }))

  if (!allProductImpressions)
    impressedProducts = impressedProducts.slice(-data.value.items)

  const chunkSize = 24
  chunkArray(impressedProducts, chunkSize)
    .forEach((productsChunk, i) => {
      const pushFn = immediate ? $gtm.push : $gtm.queueEvent
      pushFn('product.onProductImpression', productsChunk, route.query.filters?.toString() || '', {
        sortOption: (route.query.sort || defaultSort)?.toString() || '',
        layout: cols[$viewport.breakpoint],
        breadcrumbs: breadcrumbs.value,
        offset: query.start + i * chunkSize,
        enCategoryNameWithId: enCategoryNameWithId.value,
        ...productFinderTrackingContext.value,
        ...(history.state.redirect ? { searchTerm: history.state.searchTerm } : {})
      })
    })
}

const loadMore = () => {
  $gtm.push('plpPage.onViewMore')
  query.start += query?.count || 0
  infinityLoad.value = true
  pushImpressions({ immediate: true })
}

provide(PlpAboveGridBelowHeadingEspotContextKey, { eSpotsPending, eSpot: computed(() => eSpots.value?.['above-grid-below-heading']) })

const galleryContextProvided = computed(() => getGalleryContextKey(filters.value, breadcrumbs.value))
provide(GalleryContextKey, galleryContextProvided)

const pushInitialGtmEvents = async () => {
  // Whether the data is present immediately (SSR) or not (CSR), we check for its value
  await until(() => Boolean(data.value)).toBe(true)

  if (data.value?.products?.length) {
    trackEvent('category_view', {
      products: data.value?.products,
      categoryName: categoryTitle.value,
      categoryID: categoryId,
      categoryBreadcrumbs: breadcrumbs.value.map(({ label }) => label).join(' | '),
      productsPerPage
    })
  }

  const historyState = history.state || {}
  history.replaceState({
    ...(historyState.redirect ? historyState : {}),
    ...(productFinderExperienceName.value && { productFinderExperienceName: productFinderExperienceName.value }),
    breadcrumbs: serializeAnalyticsBreadcrumbs(breadcrumbs.value),
  }, '')
  setInteractionOrigin()

  $gtm.queueEvent('page.onLoadPageData', route, {
    pageTitle: await getPageTitle(),
    pageNameEng: breadcrumbs.value.map(({ defaultName }) => defaultName).join(' - '),
    breadcrumbs: breadcrumbs.value,
    sourceId: data.value?.responseId,
    categoryId,
    ...(history.state.redirect ? { searchResults: data.value?.total } : {})
  })

  const userEventData = await getUserEventData()
  $gtm.queueEvent('user.onLoadUserData', userEventData)
  $gtm.queueEvent('page.onRouterChange', route)
  $gtm.queueEvent('cart.onCartLoad', cart)
  pushImpressions({ allProductImpressions: true })
  $gtm.flushQueue()
  $optimizely?.dataReady(userEventData)
  trackEvent('page_view', { URL: route.fullPath })
}

const resetCall = async () => {
  query.start = 0
  query.sort = sort.value
  query.filters = filtersQuery.value
  query.storeFilter = toValue(storeFilter)
  delete query.customTrigger

  await refresh({ dedupe: 'cancel' })
}

const scrollToCategoryHeading = async () => {
  if (isServer()) return
  if (!['sm', 'md'].includes($viewport.breakpoint)) return
  if (!categoryHeadingRef.value) return

  await nextTick()

  requestAnimationFrame(() => {
    const elementTop
      = categoryHeadingRef.value!.getBoundingClientRect().top

    const offsetPosition
      = elementTop + window.scrollY - stickyHeaderHeight.value

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  })
}

watch(pending, () => {
  if (isServer() || pending.value || !data.value?.products?.length)
    return

  trackEvent('category_view', {
    products: data.value?.products,
    categoryName: categoryTitle.value,
    categoryID: categoryId,
    categoryBreadcrumbs: breadcrumbs.value.map(({ label }) => label).join(' | '),
  })
})

watch([slug, filters, sort, storeFilter, customTrigger], async (
  [newSlug, ...newValues],
  [oldSlug, ...oldValues]
) => {
  if (newSlug?.[0] !== oldSlug?.[0]) return
  if (newValues.every((newValue, i) => newValue === oldValues?.[i])) return
  if (isDeactivated.value) return

  // cover scenarios when you navigate in sameCategory and filters section is not sync
  if (Object.keys(route.query).length === 0) productListKey.value++

  query.start = 0
  query.sort = sort.value
  query.filters = filtersQuery.value
  query.storeFilter = toValue(storeFilter)

  if (customTrigger.value)
    query.customTrigger = customTrigger.value
  else
    delete query.customTrigger

  infinityLoad.value = false
  await nextTick()
  await until(pending).toBe(false)

  await scrollToCategoryHeading()
  pushImpressions({ immediate: true })
})

onMounted(() => {
  void loadProductFinderSummary()

  // Push impressions after the component is mounted without a cache
  pushInitialGtmEvents()
  injectFitFinder()
})

onActivated(async () => {
  if (!isDeactivated.value) return
  isDeactivated.value = false

  await loadProductFinderSummary()

  if (
    query.sort !== sort.value
    || query.filters !== filtersQuery.value
    || query.storeFilter !== toValue(storeFilter)
    || query.customTrigger !== customTrigger.value
  ) {
    productListKey.value++
    await resetCall()
  }
  // Push impressions after restoring from keep-alive cache
  await pushInitialGtmEvents()
  injectFitFinder()
})

onBeforeUnmount (() => data.value = null)

// Sets the flag to prevent pushing impressions on reactivation
onDeactivated(() => isDeactivated.value = true)
</script>
